import get from 'lodash/get';
import cond from 'lodash/cond';
import { createSelector } from 'reselect';
import { getInstance as getBootstrap } from 'utils/bootstrap';

import { getQuestionFormNodes } from 'common/entities/formNode/selectors';
import {
    INPUT_TYPE_TEXT,
    INPUT_TYPE_CHECKBOX,
    INPUT_TYPE_EMAIL,
    INPUT_TYPE_SELECT,
    INPUT_TYPE_RADIO,
    INPUT_TYPE_DATE,
    INPUT_TYPE_PHONE_NUMBER,
    INPUT_TYPE_URL,
    INPUT_TYPE_NUMBER,
    INPUT_TYPE_ADDRESS,
    INPUT_TYPE_FILE,
    INPUT_TYPE_HIDDEN,
    INPUT_TYPE_MULTI_SELECT,
    INPUT_TYPE_PARAGRAPH,
} from 'common/entities/formNode/inputTypes';
import {
    NODE_NAME_EMAIL,
    NODE_NAME_ATTENDING,
    NODE_NAME_ADDITIONAL_GUEST_COUNT,
} from 'common/entities/formNode/nodeNames';

import reducerNamespace from '../constants/reducerNamespace';
import { FORM_SESSIONS_STEPS } from '../constants/sessions';

/** @var {Object} - Initial empty values per question type. */
const defaultAnswerValuesByQuestionType = {
    [INPUT_TYPE_TEXT]: undefined,
    [INPUT_TYPE_CHECKBOX]: 0,
    [INPUT_TYPE_EMAIL]: undefined,
    [INPUT_TYPE_SELECT]: undefined,
    [INPUT_TYPE_RADIO]: undefined,
    [INPUT_TYPE_DATE]: undefined,
    [INPUT_TYPE_PHONE_NUMBER]: undefined,
    [INPUT_TYPE_URL]: undefined,
    [INPUT_TYPE_NUMBER]: undefined,
    [INPUT_TYPE_ADDRESS]: undefined,
    [INPUT_TYPE_FILE]: undefined,
    [INPUT_TYPE_HIDDEN]: undefined,
    [INPUT_TYPE_MULTI_SELECT]: undefined,
    [INPUT_TYPE_PARAGRAPH]: undefined,
};

// ///////////////////////////////
// Selectors

/**
 * @param  {Object} state
 * @param  {string} formId - form id
 * @return {Object}
 */
export const getFormRendererInitialValues = createSelector([getQuestionFormNodes], (formNodes) =>
    formNodes.reduce((prev, node) => {
        const initialValue = get(node, 'config.initialValue', defaultAnswerValuesByQuestionType[node.inputType]);
        return {
            ...prev,
            [node.id]: initialValue,
        };
    }, {}),
);

export const getFormInitialState = (state) => state[reducerNamespace].app.formInitialState;

export const getUploadUrl = (state) => state[reducerNamespace].app.uploadUrl;

export const getSubmitUrl = (state) => state[reducerNamespace].app.submitUrl;

export const getEventId = (state) => state[reducerNamespace].app.eventId;

export const getFormRendererFormId = (state) => state[reducerNamespace].app.id;

export const getIsUpdating = (state) => get(state[reducerNamespace], 'app.isUpdating');

export const getViewerData = (state) => state[reducerNamespace].app.viewerData;

export const getGuestAccessHash = (state) => state[reducerNamespace].app.guestAccessHash;

export const getRsvpFormId = (state) => state[reducerNamespace].app.rsvpFormId;

export const getRsvpFormName = (state) => state[reducerNamespace].app.rsvpFormName;

export const getIsMobileRsvp = (state) => state[reducerNamespace].app.isMobileRsvp;

export const getLimitOneOrderPerEmail = (state) => state[reducerNamespace].app.limitOneOrderPerEmail;

export const getLimitOneTicketTypePerEmail = (state) => state[reducerNamespace].app.limitOneTicketTypePerEmail;

export const getIsAttending = (state) => state[reducerNamespace].app.isAttending;

export const getIsRegistrationOpen = (state) => get(state[reducerNamespace], 'app.isRegistrationOpen');

/**
 * @param {Object} state
 * @param {Object} node
 * @param {String|number|null} initialValue
 * @return {bool}
 */
export const getNodeShouldBeDisabled = (state, node, initialValue) => {
    const isUpdating = getIsUpdating(state);
    const isRegistrationOpen = getIsRegistrationOpen(state);
    const bootstrap = getBootstrap();
    const rsvpClosed = bootstrap.get('splashPage.rsvpClosed');
    const isAttending = getIsAttending(state);
    return cond([
        [() => !node, () => false],
        [() => !isUpdating, () => false],
        [() => node.name === NODE_NAME_EMAIL, () => true],
        [() => rsvpClosed && !isAttending && node.name === NODE_NAME_ATTENDING, () => true],
        [
            () =>
                node.name === NODE_NAME_ADDITIONAL_GUEST_COUNT &&
                node.inputType === INPUT_TYPE_CHECKBOX &&
                !isRegistrationOpen &&
                initialValue === 0,
            () => true,
        ],
        [() => true, () => false],
    ])();
};

export const getActiveSidebarTab = (state) => get(state.formBuilder, 'app.activeSidebarTab');

export const getOpenStylingSection = (state) => get(state.formBuilder, 'app.openStylingSection');

export const getOpenTicketStylingSection = (state) => get(state.formBuilder, 'app.openTicketStylingSection');

export const getFormSessionStep = (state) =>
    get(state, [reducerNamespace, 'app', 'sessionsStep']) || FORM_SESSIONS_STEPS.GUEST_INFORMATION;

export const getFormSessionsId = (state) => get(state, [reducerNamespace, 'app', 'sessionIds']);

export const getEntitySessions = (state) => state.entities.sessions;

export const getFormSessions = createSelector([getFormSessionsId, getEntitySessions], (sessionIds, sessions) =>
    sessionIds?.map((sessionId) => sessions[sessionId]),
);
